import { TileSize } from "game-common/models"
import { SightPoint } from "game-common/ray_casting"

/**
 * Live tuning, like window.buildingShadow: window.indoorSight.crisp / .feather / .penumbra.
 * `crisp` veils whatever a building's walls hide as its own sharp layer over the lighting; `false`
 * (or window.indoorSight = false) goes back to blurring the sightline in with the lights.
 * `feather` is the blur on the veil's edge, in stage px. `penumbra` is the radius, in world px, of
 * the disc sight is treated as cast from: a ray past a door frame is sharp at the frame and softens
 * the further it gets from it. 0 leaves every ray hard.
 */
export const INDOOR_SIGHT = { crisp: true, feather: 2, penumbra: 32 }

export const indoorSightConfig = (): typeof INDOOR_SIGHT => {
    if (typeof window === "undefined") return INDOOR_SIGHT
    const w = window as any
    if (w.indoorSight === undefined) w.indoorSight = INDOOR_SIGHT
    const live = w.indoorSight
    if (live === false) return { ...INDOOR_SIGHT, crisp: false }
    return live && typeof live === "object" ? { ...INDOOR_SIGHT, ...live } : INDOOR_SIGHT
}

// getSightPolygon casts a pair of rays a hair either side of every corner. Consecutive vertices
// this close in angle are such a pair...
const CornerPair = 1e-3
// ...and they are a shadow edge when the ray past the corner lands this much further out.
const MinShadowDepth = 4
// Never open a fan wider than this, however close the eye stands to the corner.
const MaxPenumbraAngle = 0.6

// Enough steps that, smoothed by the veil's feather, the fan reads as a gradient.
export const PenumbraSlices = 12

/**
 * Triangles that soften the shadow edges of a sight polygon, seven numbers per slice:
 * x0, y0, x1, y1, x2, y2, and how much of the veil the slice lifts.
 *
 * A shadow edge is a ray grazing a corner, where the polygon steps from the corner out to whatever
 * the ray met beyond it. Seen from a disc of `radius` rather than a point, light gets past that
 * ray, more of it the further from the corner: so a fan opens from the corner toward the shaded
 * side by atan(radius / distance to the corner), lifting the veil fully along the ray and not at
 * all at the fan's far side. The polygon itself is untouched, so the walls' edges stay hard.
 */
export const penumbraFan = (polygon: SightPoint[], radius: number, out: number[] = []): number[] => {
    out.length = 0
    const count = polygon.length
    if (!(radius > 0) || count < 3) return out
    for (let i = 0; i < count; i++) {
        const a = polygon[i]
        const b = polygon[(i + 1) % count]
        let turn = b.angle - a.angle
        if (turn > Math.PI) turn -= 2 * Math.PI
        else if (turn < -Math.PI) turn += 2 * Math.PI
        if (Math.abs(turn) > CornerPair) continue
        const corner = a.param < b.param ? a : b
        const beyond = corner === a ? b : a
        const length = beyond.param - corner.param
        if (length < MinShadowDepth) continue
        // Rays on the corner's side of the pair stop at its blocker, so that is the shaded side.
        const side = Math.sign(corner === a ? -turn : turn) || 1
        const spread = Math.min(MaxPenumbraAngle, Math.atan2(radius, Math.max(1, corner.param)))
        for (let k = 0; k < PenumbraSlices; k++) {
            const from = beyond.angle + side * spread * k / PenumbraSlices
            const to = beyond.angle + side * spread * (k + 1) / PenumbraSlices
            const t = (k + 0.5) / PenumbraSlices
            out.push(
                corner.x, corner.y,
                corner.x + Math.cos(from) * length, corner.y + Math.sin(from) * length,
                corner.x + Math.cos(to) * length, corner.y + Math.sin(to) * length,
                1 - t * t * (3 - 2 * t),
            )
        }
    }
    return out
}

/** Even-odd point in polygon over a flat x,y,x,y... array. */
export const flatPolygonContains = (flat: ArrayLike<number>, x: number, y: number): boolean => {
    let inside = false
    for (let i = 0, j = flat.length - 2; i < flat.length; j = i, i += 2) {
        const xi = flat[i], yi = flat[i + 1], xj = flat[j], yj = flat[j + 1]
        if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside
    }
    return inside
}

const TileSamples = [-10, 0, 10]

/**
 * Whether sight reaches into the tile at (x, y): any of nine points spread over it lies inside the
 * polygon. A wall's occluder runs along its tile's midline, so a wall the eye can see has its near
 * half inside the polygon and one it cannot see has none of it.
 */
export const sightReachesTile = (flat: ArrayLike<number>, x: number, y: number): boolean => {
    const cx = x * TileSize + TileSize / 2
    const cy = y * TileSize + TileSize / 2
    for (const dy of TileSamples) for (const dx of TileSamples) {
        if (flatPolygonContains(flat, cx + dx, cy + dy)) return true
    }
    return false
}
